import React from "react";
import { Link } from "react-router-dom";
import {
  Server,
  Globe,
  Bot,
  Database,
  Bell,
  Image as ImageIcon,
  ShieldCheck,
  Mail,
  Receipt,
  HeartHandshake,
} from "lucide-react";
import SEO from "../components/SEO";

// Monthly run-rate breakdown. Numbers are approximate operating-cost
// estimates for the current stack. Edit here as costs evolve.
const COST_LINES = [
  {
    icon: Server,
    label: "Hosting & compute",
    amount: 49,
    detail:
      "Always-on backend (FastAPI + MongoDB), frontend CDN, automatic SSL, and the cron workers that keep the registry alive 24/7.",
  },
  {
    icon: Globe,
    label: "Custom domain + privacy WHOIS",
    amount: 3,
    detail:
      "pawsonrecord.com registration, DNS, and privacy proxy averaged over 12 months.",
  },
  {
    icon: Bot,
    label: "LLM news ingestor (Claude Sonnet 4.5)",
    amount: 18,
    detail:
      "Powers the twice-daily RSS scan that extracts new convictions from national + state animal-cruelty news feeds into your review queue. Token spend scales with how much we publish.",
  },
  {
    icon: Database,
    label: "Database & backups",
    amount: 12,
    detail:
      "MongoDB storage for 298 abuser records, pending review queue, takedown requests, push subscribers, geo-cache, and rolling daily backups.",
  },
  {
    icon: Bell,
    label: "Web push notifications",
    amount: 0,
    detail:
      "Standards-based Web Push runs on free browser infrastructure (Mozilla/Google push services). We pay $0 to alert subscribers about new convictions in their state.",
  },
  {
    icon: ImageIcon,
    label: "Image storage & og:image extraction",
    amount: 6,
    detail:
      "Caches and serves 87 booking/news photos for the convicted carousel. Auto-extracts new photos via a backend scraper when records are approved.",
  },
  {
    icon: ShieldCheck,
    label: "Legal & takedown reserve",
    amount: 25,
    detail:
      "Set aside monthly to fund any legal review of takedown requests or defamation challenges — we honor every legitimate correction, but we also defend the public record when it's accurate.",
  },
  {
    icon: Mail,
    label: "Transactional email (Resend)",
    amount: 0,
    detail:
      "Free tier covers the current volume of tipster thank-yous, sponsor receipts, and renewal reminders. We move to paid only when usage exceeds the free quota.",
  },
];

const ANNUAL_COST = COST_LINES.reduce((sum, l) => sum + l.amount, 0) * 12;
const MONTHLY_COST = COST_LINES.reduce((sum, l) => sum + l.amount, 0);

const IMPACT_TIERS = [
  {
    amount: 15,
    impact: "Pays for one full month of hosting + DNS for the entire registry.",
    eyebrow: "One month online",
  },
  {
    amount: 50,
    impact:
      "Funds 50 new LLM news scans — that's enough to surface ~3 new convictions for the public review queue.",
    eyebrow: "Three new abusers added",
  },
  {
    amount: 100,
    impact:
      "Covers a full month of every operating cost on this page. Single donors who hit this tier literally keep the lights on.",
    eyebrow: "One month covered, end-to-end",
  },
  {
    amount: 500,
    impact:
      "Funds a full quarter of operations + replenishes the legal takedown reserve so we can defend the public record if challenged.",
    eyebrow: "A quarter of runway",
  },
];

export default function WhereYourMoneyGoes() {
  return (
    <div
      className="max-w-4xl mx-auto px-6 lg:px-8 py-14"
      data-testid="transparency-page"
    >
      <SEO
        title="Where Your Money Goes · Operating Cost Transparency"
        description="Full monthly breakdown of how Paws on Record spends every donated dollar — hosting, AI ingestor, geocoding, email alerts, legal review. No salaries, no overhead."
        path="/where-your-money-goes"
      />
      <div className="label-eyebrow text-red-600">Operating Costs · Full Transparency</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
        Where your money goes.
      </h1>
      <p className="text-lg text-zinc-700 mt-5 leading-relaxed max-w-2xl">
        Every dollar donated to Paws on Record pays an actual bill. No salaries,
        no marketing budget, no overhead. Here is the entire monthly run-rate of
        the registry — itemized, public, and updated whenever a number changes.
      </p>

      <div
        className="mt-10 border-2 border-zinc-900 p-6 bg-zinc-50 grid grid-cols-2 gap-6"
        data-testid="transparency-headline-numbers"
      >
        <div>
          <div className="label-eyebrow mb-2">It costs us</div>
          <div className="font-display font-black text-5xl tracking-tighter">
            ${MONTHLY_COST}
          </div>
          <div className="text-sm text-zinc-600 mt-1">per month to operate</div>
        </div>
        <div className="border-l border-zinc-300 pl-6">
          <div className="label-eyebrow mb-2">Annualized</div>
          <div className="font-display font-black text-5xl tracking-tighter">
            ${ANNUAL_COST.toLocaleString()}
          </div>
          <div className="text-sm text-zinc-600 mt-1">
            per year to keep the registry free for every shelter
          </div>
        </div>
      </div>

      <div className="mt-12">
        <div className="label-eyebrow">§ Itemized monthly breakdown</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mt-2">
          Every line item, every dollar.
        </h2>

        <div className="mt-6 border border-zinc-900" data-testid="transparency-line-items">
          {COST_LINES.map((line, i) => (
            <div
              key={line.label}
              className={`flex items-start gap-4 p-5 ${
                i < COST_LINES.length - 1 ? "border-b border-zinc-300" : ""
              }`}
              data-testid={`transparency-line-${i}`}
            >
              <div className="w-10 h-10 bg-zinc-900 text-white flex-shrink-0 flex items-center justify-center">
                <line.icon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <h3 className="font-display font-black text-lg tracking-tight">
                    {line.label}
                  </h3>
                  <div
                    className={`font-mono text-lg font-bold ${
                      line.amount === 0 ? "text-zinc-400" : "text-zinc-900"
                    }`}
                  >
                    {line.amount === 0 ? "$0 (free tier)" : `$${line.amount}/mo`}
                  </div>
                </div>
                <p className="text-sm text-zinc-700 mt-1.5 leading-relaxed">
                  {line.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* What we DON'T spend on */}
      <div className="mt-12 border-l-4 border-red-600 bg-zinc-50 p-6">
        <div className="label-eyebrow text-red-600 mb-2">§ What we don&apos;t spend on</div>
        <h3 className="font-display font-black text-2xl tracking-tighter">
          Zero overhead. Zero salaries. Zero ads.
        </h3>
        <ul className="mt-4 space-y-2 text-zinc-700 text-[15px]">
          <li className="flex gap-2"><span className="text-red-600 font-bold">×</span> No founder salary. The team works on this in evenings and weekends.</li>
          <li className="flex gap-2"><span className="text-red-600 font-bold">×</span> No paid advertising. Reach grows by word-of-mouth, partner shelters, and the Share buttons on every record.</li>
          <li className="flex gap-2"><span className="text-red-600 font-bold">×</span> No travel, swag, conferences, or consultants.</li>
          <li className="flex gap-2"><span className="text-red-600 font-bold">×</span> No commissions to fundraisers. Direct donations via Cash App and Zelle — 100% of your tip reaches Paws on Record with no processor fees.</li>
        </ul>
      </div>

      {/* Impact tiers */}
      <div className="mt-12">
        <div className="label-eyebrow">§ What your donation does</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mt-2">
          Pick a real bill to pay.
        </h2>
        <p className="text-zinc-700 mt-2 max-w-2xl">
          We don&apos;t ask you to fund a vague mission. We ask you to cover a
          specific cost on this page. Here&apos;s exactly what each tier does:
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="transparency-impact-tiers">
          {IMPACT_TIERS.map((tier, i) => (
            <Link
              to="/donate"
              key={tier.amount}
              className="border border-zinc-900 p-5 hover:bg-zinc-900 hover:text-white transition-colors group"
              data-testid={`transparency-tier-${i}`}
            >
              <div className="label-eyebrow group-hover:text-red-400">
                {tier.eyebrow}
              </div>
              <div className="font-display font-black text-4xl tracking-tighter mt-1">
                ${tier.amount}
              </div>
              <p className="text-sm leading-relaxed mt-2 opacity-90">
                {tier.impact}
              </p>
            </Link>
          ))}
        </div>
      </div>

      {/* Primary CTA */}
      <div
        className="mt-14 border-2 border-zinc-900 bg-zinc-900 text-white p-8"
        data-testid="transparency-cta"
      >
        <div className="flex items-start gap-4">
          <HeartHandshake
            size={28}
            className="text-red-500 flex-shrink-0 mt-1"
          />
          <div>
            <div className="label-eyebrow text-red-500">Keep the registry public</div>
            <h3 className="font-display font-black text-3xl tracking-tighter mt-2">
              298 convicted abusers tracked. Zero cost to shelters.
            </h3>
            <p className="text-zinc-300 text-[15px] leading-relaxed mt-3 max-w-xl">
              Every shelter, rescue, foster screener, and adopter in America can
              use this registry for free — because donors pay the bills above. If
              you can chip in even ${MONTHLY_COST}/mo, you literally keep the
              lights on for the entire country.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to="/donate"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-bold uppercase tracking-widest text-[12px] hover:bg-red-500 transition-colors"
                data-testid="transparency-cta-donate"
              >
                Donate now →
              </Link>
              <Link
                to="/sponsors"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-white text-white font-bold uppercase tracking-widest text-[12px] hover:bg-white hover:text-zinc-900 transition-colors"
                data-testid="transparency-cta-sponsors"
              >
                Or become a sponsor →
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 border border-zinc-300 p-5 bg-white text-sm text-zinc-600 leading-relaxed flex items-start gap-3">
        <Receipt size={18} className="text-zinc-500 flex-shrink-0 mt-0.5" />
        <div>
          <strong className="text-zinc-900 uppercase text-[11px] tracking-widest font-mono block mb-1.5">
            Honest disclosure
          </strong>
          These figures are approximate operating estimates as of the most
          recent update. They will change as the registry grows, integrations
          are added, or vendors raise prices. If you ever want to see exact
          invoices, email{" "}
          <a
            href="mailto:info@pawsonrecord.com"
            className="text-red-600 underline hover:no-underline"
          >
            info@pawsonrecord.com
          </a>{" "}
          and we&apos;ll send them. Paws on Record is operated by{" "}
          <strong>Refuel LLC</strong> as a public-interest transparency project.
        </div>
      </div>
    </div>
  );
}
